'use client';

import { ReactNode, useEffect, useRef } from 'react';
import { useIntersecting } from '@/hooks/useInterSecting';

interface IInfiniteScrollProps {
  isLoading: boolean;
  hasMore: boolean;
  children: ReactNode;
  loadMoreFunc: () => void;
  loaderElement?: ReactNode;
  skipInitialLoad?: boolean;
}

export function InfiniteScroll({
  isLoading,
  hasMore,
  children,
  loadMoreFunc,
  loaderElement,
  skipInitialLoad = false,
}: Readonly<IInfiniteScrollProps>) {
  const { elementRef, isIntersecting } = useIntersecting();
  const skippedInitialLoad = useRef(false);

  useEffect(() => {
    if (!isLoading && hasMore && isIntersecting) {
      if (skipInitialLoad && !skippedInitialLoad.current) {
        skippedInitialLoad.current = true;
        return;
      }
      loadMoreFunc();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIntersecting, hasMore, skipInitialLoad]);

  return (
    <>
      {children}
      <div ref={elementRef as React.RefObject<HTMLDivElement>} style={{ minHeight: '60px' }} className="flex items-center justify-center">
        {isLoading
          ? (loaderElement ?? (
              <span
                className="inline-block h-10 w-10 animate-spin rounded-full border-[4px] border-gray-200 border-t-[#645EA4]"
                role="status"
                aria-label="Loading more"
              />
            ))
          : null}
      </div>
    </>
  );
}
